import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Card, CardHeader, CardBody } from "reactstrap";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { APIClient } from "../../helpers/api_helper";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import TableContainer from "../../components/Common/TableContainer";
import formatPersonName from "../../helpers/formatPersonName";
import { formatDateTime } from "../../helpers/dateFormat";
import "./smsLogs.css";

const api = new APIClient();

// Page and filters live in the URL (?page=2&status=FAILED&from=…), so
// "back" from a log's detail page returns to the same page and filters.
const readQuery = (search) => {
  const q = new URLSearchParams(search);
  return {
    pageParams: {
      rowsPerPage: Number(q.get("take")) || 10,
      currentPage: Number(q.get("page")) || 1,
    },
    filterParams: {
      search: q.get("q") || "",
      status: q.get("status") || "",
      date: {
        start: q.get("from") || undefined,
        end: q.get("to") || undefined,
      },
    },
  };
};

const writeQuery = (pageParams, filterParams) => {
  const q = new URLSearchParams();
  if (pageParams.currentPage > 1) q.set("page", pageParams.currentPage);
  if (pageParams.rowsPerPage !== 10) q.set("take", pageParams.rowsPerPage);
  if (filterParams.search) q.set("q", filterParams.search);
  if (filterParams.status && filterParams.status !== "all")
    q.set("status", filterParams.status);
  if (filterParams.date?.start) q.set("from", filterParams.date.start);
  if (filterParams.date?.end) q.set("to", filterParams.date.end);
  const qs = q.toString();
  return qs ? `?${qs}` : "";
};

const SmsLog = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [smsLogs, setSmsLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageParams, setPageParamsState] = useState(
    () => readQuery(location.search).pageParams
  );
  const [filterParams, setFilterParamsState] = useState(
    () => readQuery(location.search).filterParams
  );
  const [pageCount, setPageCount] = useState(0);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);

  const setPageParams = (params) => {
    setPageParamsState((prev) => ({ ...prev, ...params }));
  };

  // A new filter starts again from page 1.
  const setFilterParams = (params) => {
    setFilterParamsState(params);
    setPageParamsState((prev) => ({ ...prev, currentPage: 1 }));
  };

  useEffect(() => {
    const search = writeQuery(pageParams, filterParams);
    if (search !== location.search) {
      history.replace({ pathname: location.pathname, search });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageParams, filterParams]);

  const fetchSmsLogs = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_API_BASE_URL}/api/sms-logs?page=${pageParams.currentPage}&take=${pageParams.rowsPerPage}`;

      if (filterParams.search) {
        url += `&search=${encodeURIComponent(filterParams.search)}`;
      }
      if (filterParams.status && filterParams.status !== "all") {
        url += `&deliveryStatus=${encodeURIComponent(filterParams.status)}`;
      }
      // Whole days in Taiwan time
      if (filterParams.date?.start) {
        url += `&sentAtFrom=${encodeURIComponent(
          `${filterParams.date.start}T00:00:00+08:00`
        )}`;
      }
      if (filterParams.date?.end) {
        url += `&sentAtTo=${encodeURIComponent(
          `${filterParams.date.end}T23:59:59.999+08:00`
        )}`;
      }

      const response = await api.get(url);
      if (response?.data) {
        setSmsLogs(response.data);
        if (response.meta) {
          setPageCount(response.meta.pageCount || 0);
          setHasPreviousPage(response.meta.hasPreviousPage || false);
          setHasNextPage(response.meta.hasNextPage || false);
        }
      }
    } catch (error) {
      console.error("Error fetching SMS logs:", error);

      // Check if this is an auto logout error from API helper
      if (error?.autoLogout) {
        console.log(
          "[SmsLogs] Auto logout detected, preserving current UI state"
        );
        // Don't clear data or show error toast - API helper handles this
        return;
      }

      // For other errors, clear the data
      console.warn("[SmsLogs] Non-auth error, clearing SMS logs data");
      setSmsLogs([]);
    } finally {
      setLoading(false);
    }
  }, [pageParams, filterParams]);

  useEffect(() => {
    fetchSmsLogs();
  }, [fetchSmsLogs]);

  const handleRowClick = (row) => {
    history.push(`/sms-logs/${row.id}`, {
      from: `${location.pathname}${location.search}`,
    });
  };

  const smsLogsColumns = useMemo(
    () => [
      {
        Header: t("smsLogs.columns.workflowId"),
        accessor: "workflowId",
        width: "12%",
      },
      {
        Header: t("smsLogs.columns.recipientName"),
        accessor: "recipientsFirstName",
        width: "15%",
        Cell: ({ row }) => {
          const firstName = row.original.recipientsFirstName || "";
          const lastName = row.original.recipientsLastName || "";
          return <div>{formatPersonName(firstName, lastName)}</div>;
        },
      },
      {
        Header: t("smsLogs.columns.recipientPhone"),
        accessor: "phoneNumber",
        width: "18%",
        Cell: ({ value }) => value || "-",
      },
      {
        // One line; the full text shows on hover and on the detail page.
        Header: t("smsLogs.columns.message"),
        accessor: "body",
        width: "26%",
        Cell: ({ value }) => (
          <div className="sms-log-message" title={value || ""}>
            {value || "-"}
          </div>
        ),
      },
      {
        Header: t("smsLogs.columns.sentAt"),
        accessor: "sentAt",
        width: "16%",
        Cell: ({ value }) =>
          value ? formatDateTime(value, i18n.language) : "-",
      },
      {
        Header: t("smsLogs.columns.deliveryStatus"),
        accessor: "deliveryStatus",
        width: "11%",
        Cell: ({ value }) => {
          // Treat null/empty and "unknown" as pending
          const status = (value || "").toLowerCase();
          if (status === "delivered" || status === "success") {
            return (
              <span className="status-badge status-badge-active">
                {t("common.smsStatus.DELIVERED")}
              </span>
            );
          }
          // Stopped before reaching EVERY8D: no points, not counted
          if (status === "not_sent") {
            return (
              <span className="status-badge status-badge-inactive">
                {t("common.smsStatus.NOT_SENT")}
              </span>
            );
          }
          if (!status || status === "pending" || status === "unknown") {
            return (
              <span className="status-badge status-badge-pending">
                {t("common.smsStatus.PENDING")}
              </span>
            );
          }
          return (
            <span className="status-badge status-badge-failed">
              {t("common.smsStatus.FAILED")}
            </span>
          );
        },
      },
    ],
    [t, i18n.language]
  );

  // for the select filter
  const sendingStatusOptions = [
    {
      options: [
        { label: t("common.labels.all"), value: "all" },
        { label: t("common.smsStatus.DELIVERED"), value: "DELIVERED" },
        { label: t("common.smsStatus.PENDING"), value: "PENDING" },
        { label: t("common.smsStatus.FAILED"), value: "FAILED" },
        { label: t("common.smsStatus.NOT_SENT"), value: "NOT_SENT" },
      ],
    },
  ];

  document.title = t("common.pageTitle", { page: t("smsLogs.pageTitle") });

  return (
    <React.Fragment>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Row>
        <Col lg={12}>
          <Card id="customerList">
            <CardHeader className="border-0">
              <Row className="g-4 align-items-center">
                <div className="col-sm">
                  <div>
                    <h5 className="card-title mb-0">
                      {t("smsLogs.listTitle")}
                    </h5>
                  </div>
                </div>
              </Row>
            </CardHeader>

            <CardBody className="pt-0">
              <TableContainer
                columns={smsLogsColumns}
                data={smsLogs}
                loading={loading}
                pageCount={pageCount}
                hasPreviousPage={hasPreviousPage}
                hasNextPage={hasNextPage}
                pageParams={pageParams}
                setPageParams={setPageParams}
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                onRowClick={handleRowClick}
                disableRowModal={true}
                datePicker={true}
                selectFilterOptions={sendingStatusOptions}
                isGlobalFilter={true}
                isGlobalSearch={true}
                tableClass="custom-header-css"
                theadClass="text-muted"
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default SmsLog;
